/* Layer order, as in tokens.css: in production this file can load before it. */
@layer gilt.tokens, gilt.base, gilt.components, gilt.utilities;

@layer gilt.components {
  /* ============================================================ Input */
  .g-input {
    --in-h: var(--h-md);
    --in-px: 10px;
    --in-font: var(--text-sm);

    display: flex;
    align-items: center;
    gap: 6px;
    height: var(--in-h);
    padding: 0 var(--in-px);
    background: var(--bg-sunken);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    color: var(--fg);
    font-size: var(--in-font);
    transition:
      border-color var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out),
      background-color var(--dur-fast);
    min-width: 0;
  }
  .g-input:hover {
    border-color: var(--border-strong);
  }
  .g-input:focus-within {
    border-color: var(--accent-border);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  .g-input[data-invalid] {
    border-color: var(--danger-border);
  }
  .g-input[data-invalid]:focus-within {
    box-shadow: 0 0 0 3px var(--danger-soft);
  }
  .g-input[data-disabled] {
    opacity: 0.55;
  }
  .g-input[data-size='sm'] {
    --in-h: var(--h-sm);
    --in-px: 8px;
    --in-font: var(--text-sm);
    border-radius: 7px;
  }
  .g-input[data-size='lg'] {
    --in-h: var(--h-lg);
    --in-px: 12px;
    --in-font: var(--text-md);
    border-radius: 10px;
  }
  .g-input[data-variant='ghost'] {
    background: transparent;
    border-color: transparent;
  }
  .g-input[data-variant='ghost']:hover {
    background: var(--bg-hover);
    border-color: transparent;
  }
  .g-input[data-variant='ghost']:focus-within {
    background: var(--bg-sunken);
    border-color: var(--accent-border);
  }
  .g-input__control {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    padding: 0;
    font-size: inherit;
  }
  .g-input__control::placeholder,
  .g-textarea::placeholder {
    color: var(--fg-faint);
  }
  .g-input__control:focus-visible {
    outline: none;
  }
  .g-input__adorn {
    display: inline-flex;
    align-items: center;
    color: var(--fg-subtle);
    font-size: var(--text-xs);
    flex-shrink: 0;
  }
  .g-input__adorn svg {
    width: 15px;
    height: 15px;
  }
  /* Hide number spinners; values are scrubbed or typed. */
  .g-input__control[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  .g-input__control[type='number']::-webkit-inner-spin-button,
  .g-input__control[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* ============================================================ Textarea */
  .g-textarea {
    display: block;
    width: 100%;
    min-height: 72px;
    padding: 8px 10px;
    background: var(--bg-sunken);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    resize: vertical;
    transition:
      border-color var(--dur-fast),
      box-shadow var(--dur-fast);
  }
  .g-textarea:hover {
    border-color: var(--border-strong);
  }
  .g-textarea:focus {
    outline: none;
    border-color: var(--accent-border);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  .g-textarea[data-variant='ghost'] {
    background: transparent;
    border-color: transparent;
    resize: none;
    padding-inline: 0;
  }
  .g-textarea[data-variant='ghost']:focus {
    box-shadow: none;
    border-color: transparent;
  }

  /* ============================================================ Field */
  .g-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .g-field__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .g-field__label {
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    color: var(--fg-muted);
  }
  .g-field__req {
    color: var(--accent-text);
    margin-left: 2px;
  }
  .g-field__aside {
    font-size: var(--text-xs);
    color: var(--fg-subtle);
  }
  .g-field__hint,
  .g-field__error {
    font-size: var(--text-xs);
    color: var(--fg-subtle);
    line-height: var(--leading-snug);
  }
  .g-field__error {
    color: var(--danger-text);
  }

  /* ============================================================ Checkbox */
  .g-checkbox-wrap {
    display: inline-flex;
  }
  .g-checkbox {
    --cb: 16px;
    appearance: none;
    flex-shrink: 0;
    width: var(--cb);
    height: var(--cb);
    margin: 0;
    border-radius: 5px;
    border: 1.5px solid var(--border-strong);
    background: var(--bg-sunken);
    display: inline-grid;
    place-content: center;
    cursor: pointer;
    transition:
      background-color var(--dur-fast),
      border-color var(--dur-fast),
      box-shadow var(--dur-fast);
  }
  .g-checkbox[data-size='sm'] {
    --cb: 14px;
    border-radius: 4px;
  }
  .g-checkbox:hover {
    border-color: var(--fg-subtle);
  }
  .g-checkbox::after {
    content: '';
    width: calc(var(--cb) - 5px);
    height: calc(var(--cb) - 5px);
    background: var(--accent-fg);
    clip-path: polygon(14% 52%, 0 66%, 38% 100%, 100% 26%, 86% 12%, 38% 72%);
    transform: scale(0);
    transition: transform var(--dur-fast) var(--ease-spring);
  }
  .g-checkbox:checked,
  .g-checkbox:indeterminate {
    background: var(--accent);
    border-color: var(--accent);
  }
  .g-checkbox:checked::after {
    transform: scale(1);
  }
  .g-checkbox:indeterminate::after {
    clip-path: inset(40% 10% 40% 10%);
    transform: scale(1);
  }
  .g-checkbox:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .g-choice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    min-width: 0;
  }
  .g-choice .g-checkbox {
    margin-top: 2px;
  }
  .g-choice__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .g-choice__label {
    font-size: var(--text-sm);
    color: var(--fg);
  }
  .g-choice__desc {
    font-size: var(--text-xs);
    color: var(--fg-subtle);
    line-height: var(--leading-snug);
  }
  .g-choice--switch {
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .g-choice[data-disabled] {
    opacity: 0.55;
    cursor: default;
  }

  /* ============================================================ Switch */
  .g-switch {
    --sw-w: 34px;
    --sw-h: 20px;
    position: relative;
    flex-shrink: 0;
    width: var(--sw-w);
    height: var(--sw-h);
    border-radius: 999px;
    background: var(--bg-active);
    border: 1px solid var(--border);
    transition: background-color var(--dur-base) var(--ease-out);
  }
  .g-switch[data-size='sm'] {
    --sw-w: 28px;
    --sw-h: 16px;
  }
  .g-switch__thumb {
    position: absolute;
    top: 50%;
    left: 2px;
    width: calc(var(--sw-h) - 6px);
    height: calc(var(--sw-h) - 6px);
    border-radius: 999px;
    background: var(--fg-muted);
    transform: translateY(-50%);
    transition:
      transform var(--dur-base) var(--ease-spring),
      background-color var(--dur-base);
    box-shadow: var(--shadow-xs);
  }
  .g-switch[aria-checked='true'] {
    background: var(--accent);
    border-color: var(--accent);
  }
  .g-switch[aria-checked='true'] .g-switch__thumb {
    background: var(--accent-fg);
    transform: translate(calc(var(--sw-w) - var(--sw-h)), -50%);
  }
  .g-switch:disabled {
    opacity: 0.5;
  }

  /* ============================================================ Segmented control */
  .g-segmented {
    --seg-h: var(--h-md);
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 2px;
    height: var(--seg-h);
    gap: 2px;
    background: var(--bg-sunken);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    isolation: isolate;
  }
  .g-segmented[data-size='sm'] {
    --seg-h: var(--h-sm);
    border-radius: 7px;
  }
  .g-segmented--block {
    display: flex;
  }
  .g-segmented--block .g-segmented__item {
    flex: 1;
  }
  .g-segmented__thumb {
    position: absolute;
    top: 2px;
    bottom: 2px;
    left: 0;
    border-radius: calc(var(--radius-md) - 2px);
    background: var(--bg-raised);
    border: 1px solid var(--border);
    box-shadow: var(--highlight), var(--shadow-xs);
    transition:
      transform var(--dur-base) var(--ease-out),
      width var(--dur-base) var(--ease-out);
    z-index: -1;
  }
  .g-segmented__item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 100%;
    padding: 0 10px;
    border-radius: calc(var(--radius-md) - 2px);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--fg-subtle);
    transition: color var(--dur-fast);
    white-space: nowrap;
  }
  .g-segmented__item svg {
    width: 15px;
    height: 15px;
  }
  .g-segmented__item:hover {
    color: var(--fg-muted);
  }
  .g-segmented__item[aria-checked='true'] {
    color: var(--fg);
  }

  /* ============================================================ Tabs */
  .g-tabs {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    border-bottom: 1px solid var(--border-subtle);
    min-width: 0;
  }
  .g-tabs__list {
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
    overflow-x: auto;
    scrollbar-width: none;
    min-width: 0;
  }
  .g-tabs__list::-webkit-scrollbar {
    display: none;
  }
  .g-tabs__tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 10px;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    color: var(--fg-subtle);
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
    white-space: nowrap;
    transition: color var(--dur-fast);
  }
  .g-tabs[data-size='sm'] .g-tabs__tab {
    height: 32px;
    font-size: var(--text-xs);
  }
  .g-tabs__tab svg {
    width: 15px;
    height: 15px;
  }
  .g-tabs__tab:hover {
    color: var(--fg-muted);
  }
  .g-tabs__tab[aria-selected='true'] {
    color: var(--fg);
  }
  .g-tabs__count {
    font-size: var(--text-2xs);
    color: var(--fg-subtle);
    background: var(--bg-active);
    padding: 1px 6px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
  }
  .g-tabs__bar {
    position: absolute;
    left: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px;
    background: var(--accent);
    transition:
      transform var(--dur-base) var(--ease-out),
      width var(--dur-base) var(--ease-out);
  }
  .g-tabs__after {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;
    padding-bottom: 4px;
  }

  /* ============================================================ Select trigger */
  .g-select {
    --sel-h: var(--h-md);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: var(--sel-h);
    padding: 0 8px 0 10px;
    min-width: 0;
    max-width: 100%;
    background: var(--bg-sunken);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    color: var(--fg);
    text-align: left;
    transition:
      border-color var(--dur-fast),
      background-color var(--dur-fast);
  }
  .g-select:hover {
    border-color: var(--border-strong);
  }
  .g-select[aria-expanded='true'] {
    border-color: var(--accent-border);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  .g-select[data-size='sm'] {
    --sel-h: var(--h-sm);
    border-radius: 7px;
  }
  .g-select[data-size='lg'] {
    --sel-h: var(--h-lg);
  }
  .g-select[data-variant='ghost'] {
    background: transparent;
    border-color: transparent;
    color: var(--fg-muted);
  }
  .g-select[data-variant='ghost']:hover {
    background: var(--bg-hover);
    color: var(--fg);
  }
  .g-select[data-placeholder] .g-select__value {
    color: var(--fg-faint);
  }
  .g-select__value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .g-select__icon {
    display: inline-flex;
    color: var(--fg-muted);
  }
  .g-select__icon svg {
    width: 15px;
    height: 15px;
  }
  .g-select__chevron {
    color: var(--fg-subtle);
    flex-shrink: 0;
  }
  .g-select:disabled {
    opacity: 0.5;
  }
}
